import ConnectingIndicator from '@components/ConnectingIndicator/ConnectingIndicator';
import noop from '@lib/utils/noop';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import React from 'react';
import { TaskInboundActionsRow } from '../../TaskVoiceCard/TaskVoiceActions/TaskInboundActionsRow';
import { taskStrings } from '@components/Task';
import { isVoiceTask } from '@modules/InboxModule/taskTypes/voiceTypes';
import useBrowserState from '@modules/BrowserModule/useBrowserState';
import { MicrophoneAccessType } from '@modules/BrowserModule/types';

type Props = {
  offered: boolean;
  type: string;
  callConnecting?: boolean;
  isAnswering: boolean;
  onAccept?: () => void;
  onReject?: () => void;
};

const ActionsRow = ({
  offered,
  type,
  callConnecting = false,
  isAnswering,
  onAccept = noop,
  onReject = noop,
}: Props) => {
  const { microphoneAccess } = useBrowserState();
  const { phoneConnected: connected, callList } = usePhoneState();
  const {
    [FeatureFlagKeys.featureGracefullyHandleMicrophoneDisconnect]:
      hasGracefullyHandleMicrophoneDisconnect,
  } = useFeatureFlags();

  if (!offered && !callConnecting) {
    return null;
  }

  const isVoice = isVoiceTask(type);

  const voiceAcceptText =
    Object.values(callList).length > 1
      ? taskStrings.holdAndAccept
      : taskStrings.acceptCall;
  const acceptText = isVoice ? voiceAcceptText : taskStrings.accept;

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAccept();
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReject();
  };

  const isControlDisabled = (!connected && isVoice) || isAnswering;
  const hasMicrophoneAccess =
    microphoneAccess === MicrophoneAccessType.ACCESS_GRANTED;
  const isAcceptDisabled =
    isControlDisabled ||
    // flag must be enabled to block accept if microphone access is not granted
    (!!hasGracefullyHandleMicrophoneDisconnect &&
      isVoice &&
      !hasMicrophoneAccess);
  let acceptTooltip: string | undefined;
  if (hasGracefullyHandleMicrophoneDisconnect && !hasMicrophoneAccess) {
    acceptTooltip = taskStrings.noMicrophone;
  }

  if (callConnecting) {
    return <ConnectingIndicator />;
  }

  return (
    <TaskInboundActionsRow
      disabled={isControlDisabled}
      acceptDisabled={isAcceptDisabled}
      acceptText={acceptText}
      rejectText={taskStrings.reject}
      handleAccept={handleAccept}
      handleReject={handleReject}
      acceptTooltip={acceptTooltip}
    />
  );
};

export default ActionsRow;
